import { ButtonHTMLAttributes, ReactNode, CSSProperties } from 'react'
import clsx from 'clsx'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'danger' | 'ghost' | 'success'
  size?: 'sm' | 'md' | 'lg'
  isLoading?: boolean
  children: ReactNode
}

/**
 * Common Button — adapter over Serac's 5 canonical button variants.
 *
 * The 6-variant API stays the same so existing call sites compile, but
 * each variant maps to a Serac variant under the hood:
 *   primary   → Serac ice    (flat ice cyan, dark text, ice glow)
 *   secondary → Serac quiet  (surface-2 with border)
 *   outline   → Serac ghost  (transparent with strong border)
 *   ghost     → Serac quiet-text (transparent, no border)
 *   danger    → Serac danger
 *   success   → Serac ok-signal (signal-ok bg, dark text)
 *
 * All variants use the same crisp Serac r-2 (6px) radius, no pill shapes,
 * no gradients on primary surfaces (per SKILL anti-pattern).
 */
export default function Button({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  disabled,
  className,
  children,
  style,
  ...props
}: ButtonProps) {
  const baseStyles =
    'relative inline-flex items-center justify-center gap-2 font-medium whitespace-nowrap select-none transition-[background-color,color,transform,box-shadow] duration-120 ease-out focus:outline-none btn-focus disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none active:scale-[0.98]'

  const sizeStyles: Record<NonNullable<ButtonProps['size']>, string> = {
    sm: 'px-2.5 h-7 text-[13px] tracking-[-0.005em] rounded-md',
    md: 'px-3.5 h-[34px] text-[14px] tracking-[-0.005em] rounded-md',
    lg: 'px-5 h-11 text-[15px] tracking-[-0.008em] rounded-md',
  }

  // Variant-specific styling — all values resolve via theme tokens so
  // dark/light flips automatically. Hover deltas come from --surface-2
  // (one stratum brighter) so they stay subtle in both themes.
  const variantStyles: Record<NonNullable<ButtonProps['variant']>, CSSProperties> = {
    primary: {
      background: 'var(--ice-500)',
      color: 'var(--ice-on-fill)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35), var(--glow-ice)',
    },
    secondary: {
      background: 'var(--surface-2)',
      color: 'var(--fg)',
      border: '1px solid var(--border)',
    },
    outline: {
      background: 'transparent',
      color: 'var(--fg)',
      border: '1px solid var(--border-strong)',
    },
    ghost: {
      background: 'transparent',
      color: 'var(--fg-muted)',
    },
    danger: {
      background: 'var(--tint-fail-bg)',
      color: 'var(--tint-fail-fg)',
      border: '1px solid var(--tint-fail-br)',
    },
    success: {
      background: 'var(--signal-ok)',
      color: 'var(--ice-on-fill)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35)',
    },
  }

  return (
    <button
      className={clsx(baseStyles, sizeStyles[size], className)}
      style={{ ...variantStyles[variant], ...style }}
      disabled={disabled || isLoading}
      aria-busy={isLoading}
      onMouseEnter={(e) => {
        if (variant === 'ghost') {
          e.currentTarget.style.background = 'var(--surface-2)'
          e.currentTarget.style.color = 'var(--fg)'
        } else if (variant === 'primary') {
          e.currentTarget.style.background = 'var(--ice-400)'
        } else if (variant === 'secondary' || variant === 'outline') {
          e.currentTarget.style.background = 'var(--border)'
        }
      }}
      onMouseLeave={(e) => {
        const v = variantStyles[variant]
        e.currentTarget.style.background = (v.background as string) ?? ''
        if (variant === 'ghost') {
          e.currentTarget.style.color = 'var(--fg-muted)'
        }
      }}
      {...props}
    >
      {isLoading ? (
        <>
          <svg
            className="animate-spin -ml-0.5 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span>Loading…</span>
        </>
      ) : (
        children
      )}
    </button>
  )
}
